import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchChurchAssignedTemplates, fetchDistrictAssignedTemplates, fetchNationAssignedTemplates } from '@features/church/api/churchApi';
import { AssignedTemplate, DepartmentWithTemplates } from '../types/assignedTemplates';
import { fetchAllPages } from '@core/utils/fetchAllPages';
import { buildDefaultListParams } from '@core/utils/listQuery';

interface UseAssignedTemplatesOptions {
  churchId?: number | undefined;
  districtId?: number | undefined;
  nationId?: number | undefined;
  enabled?: boolean;
  /** Freetext search (department name, template name/description, etc.). */
  freetext?: string;
}

interface UseAssignedTemplatesReturn {
  departmentsWithTemplates: DepartmentWithTemplates[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

function groupTemplatesByDepartment(templates: AssignedTemplate[]): DepartmentWithTemplates[] {
  const byDept = new Map<number, DepartmentWithTemplates>();

  for (const template of templates) {
    const existing = byDept.get(template.department_id);
    if (existing) {
      existing.templates.push(template);
    } else {
      byDept.set(template.department_id, {
        department_id: template.department_id,
        department_name: template.department_name,
        templates: [template],
      });
    }
  }

  for (const dept of byDept.values()) {
    dept.templates.sort((a, b) => a.list_order - b.list_order);
  }

  return [...byDept.values()].sort((a, b) =>
    a.department_name.localeCompare(b.department_name)
  );
}

export function useAssignedTemplates({
  churchId,
  districtId,
  nationId,
  enabled = true,
  freetext,
}: UseAssignedTemplatesOptions): UseAssignedTemplatesReturn {
  const numericChurchId = churchId && churchId > 0 ? churchId : undefined;
  const numericDistrictId = districtId && districtId > 0 ? districtId : undefined;
  const numericNationId = nationId && nationId > 0 ? nationId : undefined;

  const { scope, scopeId } = useMemo(() => {
    if (numericChurchId) return { scope: 'CHURCH' as const, scopeId: numericChurchId };
    if (numericDistrictId) return { scope: 'DISTRICT' as const, scopeId: numericDistrictId };
    if (numericNationId) return { scope: 'NATION' as const, scopeId: numericNationId };
    return { scope: null, scopeId: undefined };
  }, [numericChurchId, numericDistrictId, numericNationId]);

  const [departmentsWithTemplates, setDepartmentsWithTemplates] = useState<DepartmentWithTemplates[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchToken, setRefetchToken] = useState(0);

  const trimmedFreetext = freetext?.trim() || undefined;

  useEffect(() => {
    if (!enabled || !scope || !scopeId) {
      setDepartmentsWithTemplates([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);

      try {
        const queryParams = buildDefaultListParams(
          {
            ...(trimmedFreetext ? { freetext: trimmedFreetext } : {}),
            all: true,
          },
          ['freetext']
        );

        const fetchFn = (page: number) => {
          if (scope === 'CHURCH') {
            return fetchChurchAssignedTemplates(scopeId, { ...queryParams, page });
          }
          if (scope === 'DISTRICT') {
            return fetchDistrictAssignedTemplates(scopeId, { ...queryParams, page });
          }
          return fetchNationAssignedTemplates(scopeId, { ...queryParams, page });
        };

        const templates = await fetchAllPages<AssignedTemplate>(fetchFn);

        if (!cancelled) {
          setDepartmentsWithTemplates(groupTemplatesByDepartment(templates));
        }
      } catch {
        if (!cancelled) {
          setError('Failed to load assigned report templates.');
          setDepartmentsWithTemplates([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [enabled, scope, scopeId, trimmedFreetext, refetchToken]);

  const refetch = useCallback(() => {
    setRefetchToken((t) => t + 1);
  }, []);

  return {
    departmentsWithTemplates,
    loading,
    error,
    refetch,
  };
}
